import React, { useRef } from "react";
import { motion, useInView } from "framer-motion";
import ConversionPage from "@/components/ConversionPage";
import { Zap, Shield, Timer, TrendingUp } from "lucide-react";

const FadeIn = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 40 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.7, delay, ease: "easeOut" }} className={className}>
      {children}
    </motion.div>
  );
};

const VipSection = () => (
  <section className="max-w-5xl mx-auto px-6 py-24">
    <FadeIn>
      <p className="text-xs tracking-[0.3em] uppercase text-rose-400 font-semibold mb-3">Our Programming Method</p>
      <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight leading-snug mb-6">
        Most programs build one thing<br />at the expense of everything else.
      </h2>
      <p className="text-white/60 text-base max-w-2xl leading-relaxed mb-4">
        Traditional periodization trains qualities in phases — strength one month, power the next, speed after that. The problem? By the time you're fast, you've lost the strength that made you fast in the first place.
      </p>
      <p className="text-white/80 text-base max-w-2xl leading-relaxed mb-12 font-semibold">
        We use Vertically Integrated Periodization (VIP) — so you never lose what you've built.
      </p>
    </FadeIn>

    {/* VIP vs Traditional comparison */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
      <FadeIn delay={0.1}>
        <div className="bg-[#0f172a] border border-[#1E293B] rounded-2xl p-6">
          <p className="text-xs tracking-[0.2em] uppercase text-white/30 font-semibold mb-4">Traditional Periodization</p>
          <div className="space-y-3">
            {[
              { month: "Month 1-2", focus: "Hypertrophy only", lost: "" },
              { month: "Month 3-4", focus: "Strength only", lost: "Muscle starts fading" },
              { month: "Month 5-6", focus: "Power only", lost: "Strength drops" },
              { month: "Game day", focus: "Fast but weaker", lost: "Back to square one" },
            ].map((row, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                <div>
                  <span className="text-white/70 text-sm font-medium">{row.month}</span>
                  <span className="text-white/40 text-xs ml-2">{row.focus}</span>
                </div>
                {row.lost && <span className="text-red-400/70 text-xs">{row.lost}</span>}
              </div>
            ))}
          </div>
        </div>
      </FadeIn>

      <FadeIn delay={0.2}>
        <div className="bg-rose-500/5 border border-rose-500/20 rounded-2xl p-6">
          <p className="text-xs tracking-[0.2em] uppercase text-rose-400 font-semibold mb-4">Paladin VIP Method</p>
          <div className="space-y-3">
            {[
              { month: "Every week", focus: "Strength + Power + Speed + Elasticity" },
              { month: "Emphasis shifts", focus: "Volume adjusts, nothing disappears" },
              { month: "Off-season", focus: "Build all qualities, emphasize strength" },
              { month: "Game day", focus: "Peak everything. Lose nothing." },
            ].map((row, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-rose-500/10 last:border-0">
                <div>
                  <span className="text-white/80 text-sm font-medium">{row.month}</span>
                  <span className="text-rose-300/60 text-xs ml-2">{row.focus}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </FadeIn>
    </div>

    {/* Why it works */}
    <FadeIn>
      <h3 className="text-lg font-bold text-white mb-6">Why VIP works</h3>
    </FadeIn>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {[
        { icon: Shield, title: "Nothing decays", desc: "Speed fades in days without stimulus. Power in a week. VIP keeps every quality alive.", color: "text-rose-400" },
        { icon: Zap, title: "Compound gains", desc: "Qualities reinforce each other. Strength feeds power. Power feeds speed. Speed feeds elasticity.", color: "text-amber-400" },
        { icon: Timer, title: "Always game-ready", desc: "No more peaking for one week and crashing the next. You're ready when it matters.", color: "text-emerald-400" },
        { icon: TrendingUp, title: "Year-round progress", desc: "Instead of building and losing, you build and keep. Every month is a new personal best.", color: "text-blue-400" },
      ].map((item, i) => (
        <FadeIn key={i} delay={i * 0.08}>
          <div className="bg-[#0f172a] border border-[#1E293B] rounded-xl p-5 h-full">
            <item.icon className={`w-6 h-6 ${item.color} mb-3`} />
            <h4 className="text-sm font-bold text-white mb-1">{item.title}</h4>
            <p className="text-white/50 text-xs leading-relaxed">{item.desc}</p>
          </div>
        </FadeIn>
      ))}
    </div>
  </section>
);

export default function SportsPerformancePage() {
  return (
    <ConversionPage
      accentColor="rose"
      badge="Accelerator  |  Sports Performance"
      headline={<>TRAIN LIKE THE<br />ATHLETES<br /><span className="text-rose-400">WE TRAIN.</span></>}
      subheadline="The same periodized, science-backed programming used by competitive athletes — built for speed, power, and dominance on the field and court. 2-6 sessions per week depending on what you choose. This isn't a template. It's a system."
      heroImage="https://images.unsplash.com/photo-1685122089874-46dd9b73b210?auto=format&fit=crop&w=1920&q=80"
      painPoints={[
        "You're training hard but not seeing the performance gains you expected on game day.",
        "Your off-season program is just random lifts with no connection to your sport.",
        "You know speed and power matter but you're only doing strength work.",
      ]}
      extraSection={<VipSection />}
      featureTitle="What's Inside the Sports Performance Accelerator"
      features={[
        { title: "2-6 Sessions Per Week", desc: "You pick the frequency. The program adapts — whether you're training 2 days or 6." },
        { title: "Vertically Integrated Programming", desc: "Strength, power, speed, and elasticity trained every week. Emphasis shifts by phase — but nothing ever drops off." },
        { title: "Speed & Plyometric Protocols", desc: "Sprint mechanics, depth jumps, reactive drills — the training most athletes skip but elites never do." },
        { title: "Auto-Progression Engine", desc: "Your weights, sets, and reps are tracked and adjusted automatically. The program adapts as you get stronger." },
        { title: "In-Depth Nutritional Guidance", desc: "Fuel for performance, not just aesthetics. Macro targets built around training demands." },
        { title: "Exercise Video Library", desc: "Coaching cues for every lift and drill so you execute with precision." },
        { title: "New Programming Blocks", desc: "Fresh phases every 4-6 weeks aligned with off-season, pre-season, and in-season demands." },
        { title: "AI Coach Access", desc: "24/7 access to an AI coach for training questions, recovery tips, and program adjustments." },
        { title: "Jump & Speed Benchmarks", desc: "Track your vertical, broad jump, sprint times, and RSI against age-group and elite standards." },
      ]}
      testimonial={{
        quote: "The difference between good and great is programming. This system is how you close that gap.",
        name: "Paladin Performance Staff",
        detail: "Built from data on 1,000+ athlete assessments",
      }}
      price="$40"
      priceInterval="week"
      priceCta="Start the Accelerator"
      priceTier="accelerator"
      faqs={[
        { q: "What is Vertically Integrated Periodization?", a: "VIP trains all athletic qualities — strength, power, speed, elasticity — every week, adjusting emphasis by phase. Unlike traditional periodization where you build one thing and lose another, VIP ensures you never lose what you've built." },
        { q: "What sports is this for?", a: "Any sport that requires speed, power, and explosiveness — basketball, volleyball, football, soccer, track & field, baseball, lacrosse, and more. The programming is built on universal athletic principles." },
        { q: "I'm in-season. Can I still use this?", a: "Yes. VIP is designed for year-round training. In-season, the emphasis shifts to maintenance and peaking while volume drops — so you stay game-ready without burning out." },
        { q: "How is this different from Foundations?", a: "Foundations builds the base. The Sports Performance Accelerator takes that base and turns it into sport-specific performance using VIP — strength, power, speed, and reactive strength trained together." },
        { q: "Can I switch from Foundations to this?", a: "Yes. Upgrade anytime from your athlete dashboard. Your Foundations subscription will be cancelled and replaced with the Accelerator." },
      ]}
      backPath="/"
    />
  );
}
